import {
  getDay,
  addDays,
  setHours,
  setMinutes,
  setSeconds,
  isBefore,
} from 'date-fns';
import * as chrono from 'chrono-node';

export const SHORTCUT_KEYS = {
  TOMORROW_MORNING: 'tomorrow_morning',
  TOMORROW_AFTERNOON: 'tomorrow_afternoon',
  MONDAY_MORNING: 'monday_morning',
  CUSTOM: 'custom',
};

const toBcp47 = locale => (locale || 'en').replace('_', '-');

export const getTomorrowDate = (now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return addDays(today, 1);
};

export const getMondayDate = (now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const dayOfWeek = getDay(now);

  if (dayOfWeek === 0) return addDays(today, 8);
  return addDays(today, 8 - dayOfWeek);
};

export const applyHour = (date, hour) =>
  setSeconds(setMinutes(setHours(new Date(date), hour), 0), 0);

export const formatHour = (hour, locale = 'en') => {
  const date = new Date(2023, 0, 1, hour, 0, 0);
  return new Intl.DateTimeFormat(toBcp47(locale), {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatShortDate = (date, locale = 'en') =>
  new Intl.DateTimeFormat(toBcp47(locale), {
    day: 'numeric',
    month: 'short',
  }).format(date);

export const getScheduleShortcuts = (now = new Date(), locale = 'en') => {
  const tomorrow = getTomorrowDate(now);
  const monday = getMondayDate(now);

  const shortcuts = [
    {
      key: SHORTCUT_KEYS.TOMORROW_MORNING,
      labelI18nKey: 'SCHEDULED_MESSAGES.MODAL.SHORTCUTS.TOMORROW_MORNING',
      date: tomorrow,
      hour: 8,
    },
    {
      key: SHORTCUT_KEYS.TOMORROW_AFTERNOON,
      labelI18nKey: 'SCHEDULED_MESSAGES.MODAL.SHORTCUTS.TOMORROW_AFTERNOON',
      date: tomorrow,
      hour: 13,
    },
    {
      key: SHORTCUT_KEYS.MONDAY_MORNING,
      labelI18nKey: 'SCHEDULED_MESSAGES.MODAL.SHORTCUTS.MONDAY_MORNING',
      date: monday,
      hour: 8,
    },
  ];

  return shortcuts
    .map(s => {
      const dateTime = applyHour(s.date, s.hour);
      const formattedDate = formatShortDate(s.date, locale);
      const formattedTime = formatHour(s.hour, locale);
      return {
        ...s,
        dateTime,
        formattedDate,
        formattedTime,
        detail: `${formattedDate}, ${formattedTime}`,
      };
    })
    .filter(s => !isBefore(s.dateTime, now));
};

export const preProcessDateInput = text => {
  let result = text;
  result = result.replace(/\bamanha\b/gi, 'amanhã');
  result = result.replace(/\bsabado\b/gi, 'sábado');
  result = result.replace(/\bproxim([ao])\b/gi, 'próxim$1');
  result = result.replace(/\bas\s+(\d)/gi, 'às $1');
  result = result.replace(/\bas\s+(manh|tard|noit)/gi, 'às $1');
  const ptWeekdays =
    '(?:segunda(?:-feira)?|ter[çc]a(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|s[áa]bado|domingo)';
  result = result.replace(
    new RegExp(`(${ptWeekdays})\\s+(?!às|as)(\\d)`, 'gi'),
    '$1 às $2'
  );
  result = result.replace(
    /(\d{1,2})h(\d{2})?(?=\s|$|,)/gi,
    (_, h, min) => `${h}:${min || '00'}`
  );
  result = result.replace(
    /(?:no per[ií]odo da|pela|de|à|às)\s+manh[ãa]/gi,
    '8:00'
  );
  result = result.replace(
    /(?:no per[ií]odo da|pela|de|à|às)\s+tarde/gi,
    '13:00'
  );
  result = result.replace(
    /(?:no per[ií]odo da|pela|de|à|às)\s+noite/gi,
    '18:00'
  );
  return result;
};

export const parseNaturalDate = (text, locale = 'en', now = new Date()) => {
  if (!text || !text.trim()) return null;
  const processed = preProcessDateInput(text.trim());
  const opts = { forwardDate: true };
  const isPt = locale.startsWith('pt');
  const primaryResults = (isPt ? chrono.pt : chrono).parse(
    processed,
    now,
    opts
  );
  const fallbackResults = (isPt ? chrono : chrono.pt).parse(
    processed,
    now,
    opts
  );
  const matchLen = results => results.reduce((s, r) => s + r.text.length, 0);
  const best =
    matchLen(primaryResults) >= matchLen(fallbackResults)
      ? primaryResults
      : fallbackResults;
  return best.length ? best[0].start.date() : null;
};

export const formatFullDateTime = (date, locale = 'en') =>
  new Intl.DateTimeFormat(toBcp47(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
